import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'

// Reusable modal overlay. Renders nothing when `open` is false.
// Closes on backdrop click or Escape unless `dismissable === false`.
function Modal({ open, onClose, children, dismissable = true }) {
  useEffect(() => {
    if (!open || !dismissable) return undefined
    function onKeyDown(e) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, dismissable, onClose])

  if (!open) return null

  return ReactDOM.createPortal(
    <div
      className="modal-backdrop"
      onClick={dismissable ? onClose : undefined}
      role="presentation"
    >
      <div
        className="modal-card"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {dismissable && (
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Chiudi"
          >
            ×
          </button>
        )}
        {children}
      </div>
    </div>,
    document.body,
  )
}

// Single-step confirmation. The user clicks once on a clear danger button
// to proceed. Use for soft-delete / restore operations.
export function ConfirmModal({
  open,
  title,
  message,
  danger = false,
  confirmLabel = 'Conferma',
  cancelLabel = 'Annulla',
  onConfirm,
  onCancel,
}) {
  return (
    <Modal open={open} onClose={onCancel}>
      {title && <h2 className="modal-title">{title}</h2>}
      {message && <p className="modal-message">{message}</p>}
      <div className="modal-actions">
        <button
          type="button"
          className="modal-btn modal-btn-secondary"
          onClick={onCancel}
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          className={
            danger ? 'modal-btn modal-btn-danger' : 'modal-btn modal-btn-primary'
          }
          onClick={onConfirm}
          autoFocus
        >
          {confirmLabel}
        </button>
      </div>
    </Modal>
  )
}

// Two-step destructive confirmation. The user must type the expected
// `confirmKeyword` (e.g. the user name) for the action button to enable.
// Use for irreversible destructive operations (delete user, drop data).
export function DoubleConfirmModal({
  open,
  title,
  message,
  confirmKeyword,
  hint,
  confirmLabel = 'Elimina definitivamente',
  cancelLabel = 'Annulla',
  onConfirm,
  onCancel,
}) {
  const [typed, setTyped] = useState('')

  useEffect(() => {
    if (open) setTyped('')
  }, [open])

  const expected = String(confirmKeyword || '').trim().toLowerCase()
  const matches = typed.trim().toLowerCase() === expected && expected.length > 0

  return (
    <Modal open={open} onClose={onCancel}>
      {title && <h2 className="modal-title">{title}</h2>}
      {message && <p className="modal-message">{message}</p>}
      <div className="modal-field">
        <label className="modal-label">
          {hint || (
            <>
              Per confermare, scrivi <b>{confirmKeyword}</b>
            </>
          )}
        </label>
        <input
          autoFocus
          type="text"
          className="modal-input"
          value={typed}
          onChange={e => setTyped(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && matches) onConfirm()
          }}
          placeholder={confirmKeyword}
        />
      </div>
      <div className="modal-actions">
        <button
          type="button"
          className="modal-btn modal-btn-secondary"
          onClick={onCancel}
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          className="modal-btn modal-btn-danger"
          onClick={onConfirm}
          disabled={!matches}
        >
          {confirmLabel}
        </button>
      </div>
    </Modal>
  )
}

// Informational alert modal. Single OK button.
export function AlertModal({
  open,
  title,
  message,
  okLabel = 'OK',
  onClose,
}) {
  return (
    <Modal open={open} onClose={onClose}>
      {title && <h2 className="modal-title">{title}</h2>}
      {message && <p className="modal-message">{message}</p>}
      <div className="modal-actions">
        <button
          type="button"
          className="modal-btn modal-btn-primary"
          onClick={onClose}
          autoFocus
        >
          {okLabel}
        </button>
      </div>
    </Modal>
  )
}

export default Modal
